/**
 * Core color math functions that convert color values between rgb to lab color space. 
 * deltaE function calculated distance between two colors in lab space
 * Author: Shaunak Vairagare
 * References: 
 * https://github.com/THEjoezack/ColorMine/blob/master/ColorMine/ColorSpaces/Comparisons/Cie94Comparison.cs
 * www.easyrgb.com
 */

module.exports = {

    lab2rgb: function (lab) {
        var y = (lab[0] + 16) / 116,
            x = lab[1] / 500 + y,
            z = y - lab[2] / 200,
            r, g, b;

        x = 0.95047 * ((x * x * x > 0.008856) ? x * x * x : (x - 16 / 116) / 7.787);
        y = 1.00000 * ((y * y * y > 0.008856) ? y * y * y : (y - 16 / 116) / 7.787);
        z = 1.08883 * ((z * z * z > 0.008856) ? z * z * z : (z - 16 / 116) / 7.787);

        r = x * 3.2406 + y * -1.5372 + z * -0.4986;
        g = x * -0.9689 + y * 1.8758 + z * 0.0415;
        b = x * 0.0557 + y * -0.2040 + z * 1.0570;

        r = (r > 0.0031308) ? (1.055 * Math.pow(r, 1 / 2.4) - 0.055) : 12.92 * r;
        g = (g > 0.0031308) ? (1.055 * Math.pow(g, 1 / 2.4) - 0.055) : 12.92 * g;
        b = (b > 0.0031308) ? (1.055 * Math.pow(b, 1 / 2.4) - 0.055) : 12.92 * b;

        return [Math.max(0, Math.min(1, r)) * 255,
        Math.max(0, Math.min(1, g)) * 255,
        Math.max(0, Math.min(1, b)) * 255]
    },

    rgb2lab: function (rgb) {
        var r = rgb[0] / 255,
            g = rgb[1] / 255,
            b = rgb[2] / 255,
            x, y, z;

        r = (r > 0.04045) ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
        g = (g > 0.04045) ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
        b = (b > 0.04045) ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

        x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
        y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.00000;
        z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;

        x = (x > 0.008856) ? Math.pow(x, 1 / 3) : (7.787 * x) + 16 / 116;
        y = (y > 0.008856) ? Math.pow(y, 1 / 3) : (7.787 * y) + 16 / 116;
        z = (z > 0.008856) ? Math.pow(z, 1 / 3) : (7.787 * z) + 16 / 116;

        return [(116 * y) - 16, 500 * (x - y), 200 * (y - z)]
    },

    /**
     * // calculate the perceptual distance between colors in CIELAB
     * @param {} labA color value
     * @param {} labB color value
     */
    deltaE: function (labA, labB) {
        var deltaL = labA[0] - labB[0];
        var deltaA = labA[1] - labB[1];
        var deltaB = labA[2] - labB[2];
        var c1 = Math.sqrt(labA[1] * labA[1] + labA[2] * labA[2]);
        var c2 = Math.sqrt(labB[1] * labB[1] + labB[2] * labB[2]);
        var deltaC = c1 - c2;
        var deltaH = deltaA * deltaA + deltaB * deltaB - deltaC * deltaC;
        deltaH = deltaH < 0 ? 0 : Math.sqrt(deltaH);
        var sc = 1.0 + 0.045 * c1;
        var sh = 1.0 + 0.015 * c1;
        var deltaLKlsl = deltaL / (1.0);
        var deltaCkcsc = deltaC / (sc);
        var deltaHkhsh = deltaH / (sh);
        var i = deltaLKlsl * deltaLKlsl + deltaCkcsc * deltaCkcsc + deltaHkhsh * deltaHkhsh;
        return i < 0 ? 0 : Math.sqrt(i);
    },

    /**
    * pixelMatches Takes two rgb pixels and checks if they match
    * @param  {[type]} pixel1rgb [description]
    * @param  {[type]} pixel2rgb [description]
    * @return {[type]}           [description]
    */
    pixelMatches: function (pixel1rgb, pixel2rgb) {
        var pixel1Lab = rgb2lab(pixel1rgb);
        var pixel2Lab = rgb2lab(pixel2rgb);
        var diff = deltaE(pixel1Lab, pixel2Lab);
        if (diff < 3.0) return true;
        else return false;

    },

    /**
    * pixelMatchesIn Takes a rgb pixel and checks if it has a match in array of Lab pixels
    * @param  {[type]} pixel1rgb [description]
    * @param  {[type]} pixelArrLab [description]
    * @return {[type]}           [description]
    */
    pixelMatchesIn: function (pixel1rgb, pixelArrLab) {
        var pixel1Lab = rgb2lab(pixel1rgb);
        for (const pixel of pixelArrLab) {
            let diff = deltaE(pixel1Lab, pixel);
            if (diff < 10.0) return true;
        }
        return false;
    },

    /**
    * pixelMatches Takes two rgb pixels and checks if they match
    * @param  {[type]} pixel1rgb [description]
    * @param  {[type]} pixel2rgb [description]
    * @return {[type]}           [description]
    */
    pixelMatchesInRGBWithThreshold: function (pixel1rgb, pixel2rgb, threshold = 10) {
        const R = 0, G = 1, B = 2;
        if (
            ((pixel2rgb[R] > pixel1rgb[R] - threshold) && (pixel2rgb[R] < pixel1rgb[R] + threshold)) &&
            ((pixel2rgb[G] > pixel1rgb[G] - threshold) && (pixel2rgb[G] < pixel1rgb[G] + threshold)) &&
            ((pixel2rgb[B] > pixel1rgb[B] - threshold) && (pixel2rgb[B] < pixel1rgb[B] + threshold))
        ) return true;

        else
            return false;
    }


}   